/*
 * BlackMirror geo client (geo.js)
 * ================================
 *
 * Self-contained, dependency-free storefront client that REPLACES the Orbe
 * Geolocation app. It does three things:
 *
 *   1. Renders the persistent header COUNTRY and LANGUAGE selectors into the
 *      old Orbe anchors (#header-country-localization,
 *      #header-language-localization) so removing Orbe never breaks the header.
 *   2. Runs the personalized "are you in the right place?" decision: redirect,
 *      banner, or off.
 *   3. Is the SINGLE owner of localization on the storefront.
 *
 * Design constraints (from the frozen spec):
 *   - Fail safe. Any network or parsing failure must do NOTHING. The page must
 *     never be blocked or broken by this script.
 *   - Single-writer / anti-bounce rule. This script NEVER writes Shopify's
 *     native `localization` cookie. Language changes navigate Shopify's locale
 *     URLs; dismissal state lives under a distinct BlackMirror key. Two writers
 *     to localization caused the 2026-05-20 market-bounce incident; we keep one.
 *   - Redirects are 302-equivalent (location.replace), never a 301. Known
 *     crawlers are exempt to protect SEO / hreflang.
 *   - Runs on EVERY custom domain for the store (no host gate beyond reading
 *     the store id from the inline config).
 *
 * The matching theme snippet (region-geo.liquid) renders the inline config and
 * loads this file via the bare {{ 'geo.js' | asset_url }} pattern.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // 0. Constants
  // ---------------------------------------------------------------------------

  // Where the inline config lives. The snippet writes window.BlackMirrorGeo and
  // a <script id="bm-geo-config"> data attributes; we read either.
  var CONFIG_GLOBAL = 'BlackMirrorGeo';
  var CONFIG_EL_ID = 'bm-geo-config';

  // localStorage keys. All BlackMirror-owned, all distinct from Shopify's
  // `localization` cookie (which we must never touch).
  var LS_PREFIX = 'bm_geo_';
  // Banner dismissal key is suffixed per destination so dismissing one banner
  // does not silence a different (later) recommendation.
  var LS_DISMISS_PREFIX = LS_PREFIX + 'banner_dismissed_';
  var DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  // sessionStorage key for the one-time-per-session redirect guard, suffixed by
  // store so multi-tab / multi-store browsing does not deadlock.
  var SS_REDIRECT_GUARD_PREFIX = LS_PREFIX + 'redirected_';

  // Browser-language auto-switch. SS_LANG_GUARD = one auto-switch per session.
  // LS_LANG_CHOICE = set once the visitor MANUALLY picks a language, so we never
  // override their explicit choice on later visits.
  var SS_LANG_GUARD = LS_PREFIX + 'lang_auto_';
  var LS_LANG_CHOICE = LS_PREFIX + 'lang_choice';

  // When a visitor MANUALLY picks a market in the header selector we tag the
  // destination with ?geo_pick=1; on arrival we persist this so the destination
  // never auto-redirects them away from the market they chose.
  var LS_MARKET_CHOICE = LS_PREFIX + 'market_choice';
  var MARKET_CHOICE_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 1 year

  // geo-config can be cached briefly client-side (non-personal, cacheable).
  var CONFIG_CACHE_PREFIX = LS_PREFIX + 'config_';
  var CONFIG_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  var FETCH_TIMEOUT_MS = 4000;

  // Known crawler / bot user-agent fragments. Lowercased substring match. These
  // visitors are NEVER redirected (protects hreflang and stops bots being
  // bounced across regional domains). List is intentionally conservative.
  var CRAWLER_UA = [
    'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
    'yandexbot', 'sogou', 'exabot', 'facebot', 'facebookexternalhit',
    'ia_archiver', 'applebot', 'petalbot', 'bytespider', 'ahrefsbot',
    'semrushbot', 'mj12bot', 'dotbot', 'rogerbot', 'screaming frog',
    'gptbot', 'ccbot', 'claudebot', 'anthropic-ai', 'perplexitybot',
    'google-inspectiontool', 'chrome-lighthouse', 'pingdom', 'uptimerobot',
    'headlesschrome', 'phantomjs', ' bot', 'bot/', 'spider', 'crawler'
  ];

  // ---------------------------------------------------------------------------
  // 1. Tiny defensive helpers (no deps)
  // ---------------------------------------------------------------------------

  // Console namespace so storefront debugging is greppable but quiet.
  function warn(msg, err) {
    try {
      if (window.console && console.warn) {
        console.warn('[bm-geo] ' + msg, err || '');
      }
    } catch (e) { /* ignore */ }
  }

  // Safe localStorage / sessionStorage access. Private-mode Safari and blocked
  // storage throw on access; we must degrade silently.
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); return true; } catch (e) { return false; }
  }
  function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, val) {
    try { window.sessionStorage.setItem(key, val); return true; } catch (e) { return false; }
  }

  // URL query param read, tolerant of malformed URLs.
  function getQueryParam(name) {
    try {
      var params = new URLSearchParams(window.location.search || '');
      return params.get(name);
    } catch (e) {
      return null;
    }
  }

  // Fetch with timeout. Returns a promise that resolves to parsed JSON, or
  // rejects. Never throws synchronously.
  function fetchJSON(url) {
    return new Promise(function (resolve, reject) {
      var done = false;
      var controller = null;
      var timer = null;

      try {
        if (typeof AbortController !== 'undefined') {
          controller = new AbortController();
        }
      } catch (e) { controller = null; }

      timer = setTimeout(function () {
        if (done) return;
        done = true;
        if (controller) { try { controller.abort(); } catch (e) {} }
        reject(new Error('timeout'));
      }, FETCH_TIMEOUT_MS);

      var opts = { method: 'GET', credentials: 'omit', mode: 'cors' };
      if (controller) opts.signal = controller.signal;

      try {
        fetch(url, opts).then(function (res) {
          if (!res || !res.ok) {
            throw new Error('bad status ' + (res ? res.status : '?'));
          }
          return res.json();
        }).then(function (json) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve(json);
        }).catch(function (err) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          reject(err);
        });
      } catch (e) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        reject(e);
      }
    });
  }

  // Read the host of a URL string safely. Returns '' on failure.
  function hostOf(urlStr) {
    try {
      return new URL(urlStr, window.location.href).host.toLowerCase();
    } catch (e) {
      return '';
    }
  }

  // Append (or replace) a query param on a URL string. Used for the cross-origin
  // redirect breadcrumb. Falls back to a plain string append on bad input.
  function appendParam(urlStr, key, val) {
    try {
      var u = new URL(urlStr, window.location.href);
      u.searchParams.set(key, val);
      return u.toString();
    } catch (e) {
      var sep = urlStr.indexOf('?') === -1 ? '?' : '&';
      return urlStr + sep + encodeURIComponent(key) + '=' + encodeURIComponent(val);
    }
  }

  function isCrawler() {
    try {
      var ua = (navigator.userAgent || '').toLowerCase();
      if (!ua) return false;
      for (var i = 0; i < CRAWLER_UA.length; i++) {
        if (ua.indexOf(CRAWLER_UA[i]) !== -1) return true;
      }
      return false;
    } catch (e) {
      // If we cannot read the UA, treat as a real user (do not block redirects
      // wholesale), but this branch is extremely unlikely.
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // 2. Read inline config + kill-switch
  // ---------------------------------------------------------------------------

  // Reads { store, enabled, apiBase } from window.BlackMirrorGeo, falling back
  // to the data attributes on the <script id="bm-geo-config"> element.
  function readConfig() {
    var cfg = {};

    // Preferred: global object set by the snippet.
    try {
      var g = window[CONFIG_GLOBAL];
      if (g && typeof g === 'object') {
        cfg.store = g.store;
        cfg.enabled = g.enabled;
        cfg.apiBase = g.apiBase;
      }
    } catch (e) { /* ignore */ }

    // Fallback: data attributes on the config element.
    try {
      var el = document.getElementById(CONFIG_EL_ID);
      if (el) {
        if (cfg.store == null && el.getAttribute('data-geo-store')) {
          cfg.store = el.getAttribute('data-geo-store');
        }
        if (cfg.apiBase == null && el.getAttribute('data-geo-api-base')) {
          cfg.apiBase = el.getAttribute('data-geo-api-base');
        }
        // data-geo-enabled="false" is an explicit client kill-switch.
        var en = el.getAttribute('data-geo-enabled');
        if (en != null) {
          cfg.enabled = !(String(en).toLowerCase() === 'false' || en === '0');
        }
      }
    } catch (e) { /* ignore */ }

    // Normalize.
    if (typeof cfg.store === 'string') cfg.store = cfg.store.trim().toLowerCase();
    if (cfg.enabled == null) cfg.enabled = true;
    if (typeof cfg.apiBase === 'string') {
      // Strip a single trailing slash so we can concatenate paths cleanly.
      cfg.apiBase = cfg.apiBase.replace(/\/+$/, '');
    }

    return cfg;
  }

  // Returns true if any kill-switch is active and the client should go inert.
  // (Server enabled:false is handled later, after geo-config loads.)
  function killSwitchActive(cfg) {
    // 1. ?geo=off in the URL (instant manual disable).
    if ((getQueryParam('geo') || '').toLowerCase() === 'off') {
      return true;
    }
    // 2. data-geo-enabled="false" / server-config enabled:false (config object).
    if (cfg.enabled === false) {
      return true;
    }
    // 3. Missing store or apiBase: we cannot safely do anything.
    if (!cfg.store) {
      warn('no store id in config; going inert');
      return true;
    }
    if (!cfg.apiBase) {
      warn('no apiBase in config; going inert');
      return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // 3. geo-config (cacheable, non-personal) -> render selectors
  // ---------------------------------------------------------------------------

  function configCacheKey(store) {
    return CONFIG_CACHE_PREFIX + store;
  }

  function readCachedConfig(store) {
    try {
      var raw = lsGet(configCacheKey(store));
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || typeof obj.t !== 'number' || !obj.v) return null;
      if (Date.now() - obj.t > CONFIG_CACHE_TTL_MS) return null;
      return obj.v;
    } catch (e) {
      return null;
    }
  }

  function writeCachedConfig(store, value) {
    try {
      lsSet(configCacheKey(store), JSON.stringify({ t: Date.now(), v: value }));
    } catch (e) { /* ignore */ }
  }

  // Fetch geo-config, using a short client cache when available.
  function loadGeoConfig(cfg) {
    var cached = readCachedConfig(cfg.store);
    if (cached) {
      // Serve cache immediately; no refetch needed within TTL.
      return Promise.resolve(cached);
    }
    var url = cfg.apiBase + '/api/public/geo-config?store=' +
      encodeURIComponent(cfg.store);
    return fetchJSON(url).then(function (json) {
      if (json && typeof json === 'object') {
        writeCachedConfig(cfg.store, json);
      }
      return json;
    });
  }

  // Ensure an anchor element exists. If the snippet already created it (orphan
  // anchor safety), reuse it; otherwise this is a no-op returning null and the
  // selector simply does not render (still fail-safe).
  function getAnchor(id) {
    try {
      return document.getElementById(id);
    } catch (e) {
      return null;
    }
  }

  // --- Live markets (all owned storefronts + EU Markets domains) -------------
  // The header country selector lists every live Speediance market, the local
  // ones first (see MARKET_ORDER per store). Picking one navigates to that
  // market's site.
  var MARKETS = {
    EU: { flag: '🇪🇺', name: 'Europe',          host: 'speediance.eu',     url: 'https://speediance.eu' },
    AT: { flag: '🇦🇹', name: 'Austria',         host: 'speediance.at',     url: 'https://speediance.at' },
    BE: { flag: '🇧🇪', name: 'Belgium',         host: 'speediance.be',     url: 'https://speediance.be' },
    CH: { flag: '🇨🇭', name: 'Switzerland',     host: 'speediance.ch',     url: 'https://speediance.ch' },
    FR: { flag: '🇫🇷', name: 'France',          host: 'speediance.fr',     url: 'https://speediance.fr' },
    IE: { flag: '🇮🇪', name: 'Ireland',         host: 'speediance.ie',     url: 'https://speediance.ie' },
    NL: { flag: '🇳🇱', name: 'Netherlands',     host: 'speediance.nl',     url: 'https://speediance.nl' },
    GB: { flag: '🇬🇧', name: 'United Kingdom',  host: 'speediance.co.uk',  url: 'https://speediance.co.uk' },
    AU: { flag: '🇦🇺', name: 'Australia',       host: 'speediance.com.au', url: 'https://speediance.com.au' },
    NZ: { flag: '🇳🇿', name: 'New Zealand',     host: 'speediance.nz',     url: 'https://speediance.nz' }
  };
  // Per source store: relevant/local markets first, then every other market.
  var MARKET_ORDER = {
    eu: ['EU', 'CH', 'NL', 'AT', 'FR', 'BE', 'IE', 'GB', 'AU', 'NZ'],
    uk: ['GB', 'IE', 'EU', 'FR', 'BE', 'NL', 'CH', 'AT', 'AU', 'NZ'],
    au: ['AU', 'NZ', 'GB', 'EU', 'IE', 'FR', 'BE', 'NL', 'CH', 'AT'],
    nz: ['NZ', 'AU', 'GB', 'EU', 'IE', 'FR', 'BE', 'NL', 'CH', 'AT']
  };
  // How many leading markets are the store's own "region" (grouped first).
  var MARKET_REL = { eu: 7, uk: 2, au: 2, nz: 1 };
  var STORE_PRIMARY = { eu: 'EU', uk: 'GB', au: 'AU', nz: 'NZ' };

  // --- Same-store sub-markets -------------------------------------------------
  // Regions that live INSIDE an existing storefront (same host) and are switched
  // via Shopify localization, NOT by navigating to another domain. Picking one
  // sets the buyer country so Shopify serves that market (its catalog / currency
  // / tax) without leaving the site. Keyed by store id:
  //   placement 'inline' -> items render right under the store's primary market.
  //   placement 'group'  -> items render in their own labelled group.
  // UK: Channel Islands (Jersey + Guernsey) on speediance.co.uk (GBP, CI catalog).
  // EU: every ACTIVE speediance.eu market with NO dedicated domain, ordered by
  //     the regions we service most. The 6 EU markets that DO have their own
  //     domain (AT/BE/CH/FR/IE/NL) stay as navigating entries in MARKET_ORDER.
  var SUBMARKETS = {
    uk: {
      placement: 'inline',
      items: [
        { code: 'JE', flag: '🇯🇪', name: 'Jersey' },
        { code: 'GG', flag: '🇬🇬', name: 'Guernsey' }
      ]
    },
    eu: {
      placement: 'group',
      groupLabel: 'More of Europe',
      // Ordered by EU orders served, trailing 12 months (Shopify, 2026-07-02).
      items: [
        { code: 'PT', flag: '🇵🇹', name: 'Portugal' },      // 166 orders
        { code: 'GR', flag: '🇬🇷', name: 'Greece' },        // 165
        { code: 'LU', flag: '🇱🇺', name: 'Luxembourg' },    // 110
        { code: 'RO', flag: '🇷🇴', name: 'Romania' },       // 109
        { code: 'CY', flag: '🇨🇾', name: 'Cyprus' },        // 81
        { code: 'BG', flag: '🇧🇬', name: 'Bulgaria' },      // 69
        { code: 'MT', flag: '🇲🇹', name: 'Malta' },         // 64 (rev tiebreak)
        { code: 'SI', flag: '🇸🇮', name: 'Slovenia' },      // 64
        { code: 'LV', flag: '🇱🇻', name: 'Latvia' },        // 44
        { code: 'LT', flag: '🇱🇹', name: 'Lithuania' },     // 38
        { code: 'HR', flag: '🇭🇷', name: 'Croatia' },       // 34
        { code: 'EE', flag: '🇪🇪', name: 'Estonia' },       // 24
        { code: 'SK', flag: '🇸🇰', name: 'Slovakia' },      // 22
        { code: 'MC', flag: '🇲🇨', name: 'Monaco' },        // 20
        { code: 'LI', flag: '🇱🇮', name: 'Liechtenstein' }  // 14
      ]
    }
  };

  // The buyer country a store defaults to when the visitor has made no explicit
  // pick. speediance.eu defaults to MC (Monaco), which is also a sub-market, so a
  // sub-market equal to this must NOT show as "selected" until explicitly chosen,
  // otherwise every default visitor would look like they picked it.
  var DEFAULT_COUNTRY = { eu: 'MC', uk: 'GB' };

  // localStorage: the sub-market ISO code the visitor EXPLICITLY chose in the
  // header selector (distinguishes a real pick from the store's default country).
  var LS_SUBMARKET_CODE = LS_PREFIX + 'submarket';

  // The Shopify localization country currently in effect (e.g. 'GB', 'JE').
  // Shopify exposes this on the storefront; null when unavailable.
  function activeCountry() {
    try {
      return (window.Shopify && Shopify.country)
        ? String(Shopify.country).toUpperCase()
        : null;
    } catch (e) { return null; }
  }

  // Which market the visitor is currently on, by host. Returns a code or null.
  function marketForHost(host) {
    var h = String(host || '').toLowerCase().replace(/^www\./, '');
    if (h === 'speediance.co.nz') return 'NZ'; // NZ alias domain.
    for (var code in MARKETS) {
      if (MARKETS.hasOwnProperty(code) && MARKETS[code].host === h) return code;
    }
    return null;
  }

  // True if the visitor manually chose a market (persisted) and we should NOT
  // auto-redirect them away from it.
  function marketChoiceActive() {
    try {
      var raw = lsGet(LS_MARKET_CHOICE);
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (!ts) return false;
      if (Date.now() - ts > MARKET_CHOICE_TTL_MS) { lsSet(LS_MARKET_CHOICE, ''); return false; }
      return true;
    } catch (e) { return false; }
  }

  // --- Reusable header dropdown (used by the market + language selectors) -----
  // opts: { ariaLabel, trigger:{flag,label}, groups:[{label, items:[{flag,label,current,onSelect}]}] }
  function mountDropdown(anchor, opts) {
    injectGeoStyle();
    var wrap = document.createElement('div');
    wrap.className = 'bm-geo-sel';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bm-geo-sel-trigger';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    if (opts.ariaLabel) btn.setAttribute('aria-label', opts.ariaLabel);
    if (opts.trigger.flag) { var tf = document.createElement('span'); tf.className = 'bm-geo-fl'; tf.textContent = opts.trigger.flag; btn.appendChild(tf); }
    var tl = document.createElement('span'); tl.className = 'bm-geo-sel-label'; tl.textContent = opts.trigger.label || ''; btn.appendChild(tl);
    var cv = document.createElement('span'); cv.className = 'bm-geo-cv'; cv.setAttribute('aria-hidden', 'true');
    cv.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
    btn.appendChild(cv);

    var menu = document.createElement('div');
    menu.className = 'bm-geo-menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    (opts.groups || []).forEach(function (g, gi) {
      if (gi > 0) { var d = document.createElement('div'); d.className = 'bm-geo-div'; menu.appendChild(d); }
      if (g.label) { var lab = document.createElement('div'); lab.className = 'bm-geo-mlabel'; lab.textContent = g.label; menu.appendChild(lab); }
      (g.items || []).forEach(function (it) {
        var row = document.createElement('button');
        row.type = 'button';
        row.className = 'bm-geo-item' + (it.current ? ' cur' : '');
        row.setAttribute('role', 'option');
        if (it.current) row.setAttribute('aria-selected', 'true');
        if (it.flag) { var f = document.createElement('span'); f.className = 'bm-geo-fl'; f.textContent = it.flag; row.appendChild(f); }
        var nm = document.createElement('span'); nm.className = 'bm-geo-nm'; nm.textContent = it.label || ''; row.appendChild(nm);
        if (it.current) { var ck = document.createElement('span'); ck.className = 'bm-geo-ck'; ck.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>'; row.appendChild(ck); }
        row.addEventListener('click', function () { closeMenu(); try { if (it.onSelect) it.onSelect(); } catch (e) { warn('select onSelect', e); } });
        menu.appendChild(row);
      });
    });

    function onOutside(e) { if (!wrap.contains(e.target)) closeMenu(); }
    function onEsc(e) { if (e.key === 'Escape' || e.keyCode === 27) closeMenu(); }
    // The menu is position:fixed and positioned here, clamped INSIDE the
    // viewport, so it never spills off the left/right edge on mobile (the
    // trigger can sit anywhere in the header).
    function positionMenu() {
      try {
        var r = btn.getBoundingClientRect();
        var vw = document.documentElement.clientWidth || window.innerWidth || 360;
        var mw = menu.offsetWidth;
        var left = r.right - mw;          // align right edges by default
        var max = vw - mw - 8;
        if (left > max) left = max;
        if (left < 8) left = 8;
        menu.style.left = left + 'px';
        menu.style.top = Math.round(r.bottom + 8) + 'px';
        var vh = document.documentElement.clientHeight || window.innerHeight || 640;
        var avail = vh - (r.bottom + 8) - 12;
        if (avail < 220) { avail = Math.max(220, vh - 100); menu.style.top = Math.round(vh - avail - 12) + 'px'; }
        menu.style.maxHeight = Math.round(avail) + 'px';
      } catch (e) { /* ignore */ }
    }
    function onScroll(e) { try { if (e && e.target && e.target !== document && e.target !== window && menu.contains(e.target)) return; } catch (err) {} closeMenu(); }
    function onResize() { positionMenu(); }
    function openMenu() {
      menu.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      positionMenu();
      setTimeout(function () {
        document.addEventListener('click', onOutside, true);
        document.addEventListener('keydown', onEsc, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
      }, 0);
    }
    function closeMenu() {
      menu.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onOutside, true);
      document.removeEventListener('keydown', onEsc, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    }
    btn.addEventListener('click', function (e) { e.preventDefault(); if (menu.hidden) openMenu(); else closeMenu(); });

    wrap.appendChild(btn);
    wrap.appendChild(menu);
    try { anchor.innerHTML = ''; anchor.appendChild(wrap); } catch (e) { warn('dropdown mount failed', e); }
  }

  function selectMarket(code, curCode) {
    if (code === curCode) return;       // already on this market.
    var m = MARKETS[code];
    if (!m) return;
    // Tag the destination so its geo.js persists the choice and does not
    // auto-redirect the visitor back out of the market they just chose.
    try { window.location.assign(appendParam(m.url, 'geo_pick', '1')); } catch (e) { warn('market nav failed', e); }
  }

  // Switch to a same-store sub-market (e.g. Channel Islands) WITHOUT leaving the
  // site, by setting Shopify's localization country. This is the ONLY place
  // geo.js writes localization, and it fires ONLY on an explicit header pick via
  // Shopify's own /localization endpoint (the sanctioned country switch) - so it
  // is a single, user-initiated writer and does NOT reintroduce the two-writer
  // auto-bounce of the 2026-05-20 incident (no code path writes localization on
  // load). Persisting LS_MARKET_CHOICE first makes the post-reload client treat
  // this as a deliberate choice and never auto-redirect the visitor away from it.
  // `clearChoice` true means "reset to the store default" (returning to the base
  // market from a sub-market): we still set localization, but forget the explicit
  // sub-market pick so the selector shows the base market, not the default country.
  function selectLocalizationCountry(code, clearChoice) {
    try {
      lsSet(LS_MARKET_CHOICE, String(Date.now()));
      lsSet(LS_SUBMARKET_CODE, clearChoice ? '' : String(code));
    } catch (e) { /* ignore */ }
    try {
      var form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';
      form.acceptCharset = 'UTF-8';
      form.style.display = 'none';
      function hidden(name, value) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }
      hidden('_method', 'put');
      hidden('form_type', 'localization');
      hidden('utf8', '✓');
      hidden('country_code', code);
      hidden('return_to', window.location.pathname + window.location.search);
      (document.body || document.documentElement).appendChild(form);
      form.submit();
    } catch (e) { warn('localization switch failed', e); }
  }

  // Render the market selector into #header-country-localization.
  function renderCountrySelector(config) {
    var anchor = getAnchor('header-country-localization');
    if (!anchor) return; // orphan-anchor safety: nothing to render into.
    var store = (config && config.store) || '';
    var order = MARKET_ORDER[store];
    if (!order) return;  // unknown store: render nothing.
    var relCount = MARKET_REL[store] || 0;
    var primary = STORE_PRIMARY[store] || marketForHost(window.location.host) || order[0];
    var subsCfg = SUBMARKETS[store];
    var subs = subsCfg ? (subsCfg.items || []) : [];
    var placement = subsCfg ? (subsCfg.placement || 'inline') : 'inline';
    var defCountry = DEFAULT_COUNTRY[store] || '';

    // Current selection. A same-store sub-market is "active" when Shopify's
    // localization country matches one of its codes - EXCEPT the store's default
    // country, which only counts once the visitor has explicitly chosen it
    // (speediance.eu defaults to MC/Monaco; Monaco must not show for everyone).
    var hostCode = marketForHost(window.location.host) || primary;
    var loc = activeCountry();
    var chosen = marketChoiceActive() ? (lsGet(LS_SUBMARKET_CODE) || '') : '';
    var activeSub = null;
    for (var si = 0; si < subs.length; si++) {
      if (subs[si].code === loc) {
        if (subs[si].code === defCountry && chosen !== subs[si].code) break; // default, not chosen
        activeSub = subs[si];
        break;
      }
    }
    var curCode = activeSub ? activeSub.code : hostCode;

    function marketItem(code) {
      var m = MARKETS[code];
      if (!m) return null;
      var isPrimary = (code === primary);
      return {
        flag: m.flag, label: m.name, current: (code === curCode),
        onSelect: function () {
          // Returning to the base market from a sub-market resets localization to
          // the store default and forgets the pick; any other market navigates.
          if (isPrimary && activeSub) { selectLocalizationCountry(defCountry || code, true); }
          else { selectMarket(code, curCode); }
        }
      };
    }
    function subItem(s) {
      return {
        flag: s.flag, label: s.name, current: (s.code === curCode),
        onSelect: function () { if (s.code !== curCode) selectLocalizationCountry(s.code, false); }
      };
    }
    function marketItems(codes) {
      var items = [];
      codes.forEach(function (code) { var mi = marketItem(code); if (mi) items.push(mi); });
      return items;
    }
    // Inline placement: sub-markets render right under the store's primary market.
    function expandInline(codes) {
      var items = [];
      codes.forEach(function (code) {
        var mi = marketItem(code);
        if (mi) items.push(mi);
        if (code === primary && subs.length) {
          subs.forEach(function (s) { items.push(subItem(s)); });
        }
      });
      return items;
    }

    var hasRel = (relCount > 0 && relCount < order.length);
    var groups = [];
    if (placement === 'group' && subs.length) {
      // Sub-markets get their own labelled group between the store's own region
      // and the other continents.
      if (hasRel) {
        groups.push({ label: 'Your region', items: marketItems(order.slice(0, relCount)) });
        groups.push({ label: subsCfg.groupLabel || 'More markets', items: subs.map(subItem) });
        groups.push({ label: 'All markets', items: marketItems(order.slice(relCount)) });
      } else {
        groups.push({ label: null, items: marketItems(order) });
        groups.push({ label: subsCfg.groupLabel || 'More markets', items: subs.map(subItem) });
      }
    } else if (hasRel) {
      groups.push({ label: 'Your region', items: expandInline(order.slice(0, relCount)) });
      groups.push({ label: 'All markets', items: expandInline(order.slice(relCount)) });
    } else {
      groups.push({ label: null, items: expandInline(order) });
    }

    var trigFlag = activeSub ? activeSub.flag : (MARKETS[curCode] ? MARKETS[curCode].flag : '🌍');
    var trigLabel = activeSub ? activeSub.name : (MARKETS[curCode] ? MARKETS[curCode].name : 'Region');

    mountDropdown(anchor, {
      ariaLabel: 'Select your country or region',
      trigger: { flag: trigFlag, label: trigLabel },
      groups: groups
    });
  }

  // Language display names (endonyms), keyed by primary subtag. Fallback = code.
  var LANG_LABELS = {
    en: 'English', nl: 'Nederlands', de: 'Deutsch', fr: 'Français', it: 'Italiano',
    es: 'Español', pt: 'Português', sv: 'Svenska', da: 'Dansk', no: 'Norsk',
    nb: 'Norsk', fi: 'Suomi', pl: 'Polski', cs: 'Čeština', sk: 'Slovenčina',
    sl: 'Slovenščina', hr: 'Hrvatski', hu: 'Magyar', ro: 'Română', bg: 'Български',
    el: 'Ελληνικά', et: 'Eesti', lv: 'Latviešu', lt: 'Lietuvių', mt: 'Malti',
    lb: 'Lëtzebuergesch', tr: 'Türkçe', ga: 'Gaeilge'
  };

  // The languages THIS market actually publishes, from the page's own same-host
  // hreflang tags, deduped to one entry per language (the bare language path,
  // e.g. /de over /de-de; root for the default language). This is what scopes
  // each market to the languages it serves, instead of the store-wide list.
  function marketLanguages() {
    var alts = sameHostLocaleAlternates(); // [{lang, prefix}] on the current host
    var byPrimary = {};
    for (var i = 0; i < alts.length; i++) {
      var lang = alts[i].lang;
      if (!lang || lang === 'x-default') continue;
      var primary = lang.split('-')[0];
      var prev = byPrimary[primary];
      if (!prev || alts[i].prefix.length < prev.prefix.length) {
        byPrimary[primary] = { code: primary, prefix: alts[i].prefix };
      }
    }
    var out = [];
    for (var p in byPrimary) {
      if (byPrimary.hasOwnProperty(p)) {
        out.push({ code: p, prefix: byPrimary[p].prefix, label: (LANG_LABELS[p] || p.toUpperCase()) });
      }
    }
    out.sort(function (a, b) { return a.label.localeCompare(b.label); });
    return out;
  }

  // Render the LANGUAGE selector into #header-language-localization. Shows ONLY
  // the languages this market publishes (per its hreflang); hidden when the
  // market is single-language. Switching navigates Shopify's locale URL; we
  // never write the localization cookie (single-writer rule).
  function renderLanguageSelector(config) {
    var anchor = getAnchor('header-language-localization');
    if (!anchor) return; // orphan-anchor safety.
    var langs = marketLanguages();
    if (langs.length < 2) { try { anchor.innerHTML = ''; } catch (e) { /* ignore */ } return; }

    var curPrimary = (currentLocalePrefix().split('-')[0]) || 'en';
    var curLang = null;
    for (var i = 0; i < langs.length; i++) { if (langs[i].code === curPrimary) { curLang = langs[i]; break; } }
    if (!curLang) { for (var j = 0; j < langs.length; j++) { if (langs[j].prefix === '') { curLang = langs[j]; break; } } }
    if (!curLang) curLang = langs[0];

    var items = langs.map(function (l) {
      return { flag: '', label: l.label, current: (l === curLang), onSelect: function () { selectLanguage('/' + l.prefix); } };
    });
    mountDropdown(anchor, {
      ariaLabel: 'Select language',
      trigger: { flag: '🌐', label: curLang.label },
      groups: [{ label: null, items: items }]
    });
  }

  function selectLanguage(url) {
    try {
      // Remember the explicit choice so browser-language auto-switch does not
      // override it later.
      lsSet(LS_LANG_CHOICE, '1');
      var prefix = String(url || '').replace(/^\/+|\/+$/g, '').toLowerCase();
      window.location.assign(applyLocalePrefix(prefix));
    } catch (e) { warn('language navigation failed', e); }
  }

  // ---------------------------------------------------------------------------
  // 4. geo-lookup (personalized, no cache) -> act
  // ---------------------------------------------------------------------------

  function loadGeoLookup(cfg) {
    var url = cfg.apiBase + '/api/public/geo-lookup?store=' +
      encodeURIComponent(cfg.store);
    // Personalized + no-cache. Hint the browser/CDN not to cache.
    return fetchJSON(url + (url.indexOf('?') === -1 ? '?' : '&') + '_=' + Date.now());
  }

  // --- redirect branch -------------------------------------------------------

  function alreadyOnDestination(destinationUrl) {
    var destHost = hostOf(destinationUrl);
    if (!destHost) return true; // unparseable destination: do not redirect.
    return destHost === (window.location.host || '').toLowerCase();
  }

  function redirectGuardKey(store, destinationUrl) {
    return SS_REDIRECT_GUARD_PREFIX + store + '_' + hostOf(destinationUrl);
  }

  // Returns true if we actually performed a redirect.
  function handleRedirect(cfg, lookup) {
    var dest = lookup.destination_url;
    if (!dest) {
      warn('redirect action with no destination_url; ignoring');
      return false;
    }

    // Honor ?stay=1 (visitor explicitly chose to stay) and the cross-origin
    // breadcrumb ?bmgeo=1 (we just bounced them here from another store's geo
    // client). Either one means: never redirect again. The breadcrumb makes the
    // client loop-proof across origins even if the backend rules accidentally
    // define a reciprocal redirect pair.
    if ((getQueryParam('stay') || '') === '1' || (getQueryParam('bmgeo') || '') === '1') {
      return false;
    }

    // Respect a persisted manual market choice: if the visitor deliberately
    // picked this market in the header selector, never auto-redirect them away.
    if (marketChoiceActive()) {
      return false;
    }

    // Skip if already on the destination host (no self-redirect loop).
    if (alreadyOnDestination(dest)) {
      return false;
    }

    // Skip known crawlers entirely (SEO / hreflang protection).
    if (isCrawler()) {
      return false;
    }

    // One-time-per-session guard so a visitor who navigates Back is not
    // immediately bounced again within the same session.
    var guardKey = redirectGuardKey(cfg.store, dest);
    if (ssGet(guardKey)) {
      return false;
    }
    ssSet(guardKey, '1');

    // 302-equivalent client redirect: location.replace does not push a history
    // entry, so it behaves like a soft redirect and Back returns to the page
    // the visitor came from. Never a 301 (that would be a permanent SEO signal
    // we cannot make from the client anyway).
    try {
      // Tag the destination with the cross-origin breadcrumb so the destination
      // store's geo client will not immediately bounce the visitor back.
      window.location.replace(appendParam(dest, 'bmgeo', '1'));
      return true;
    } catch (e) {
      warn('redirect failed', e);
      return false;
    }
  }

  // --- banner branch ---------------------------------------------------------

  function dismissKey(store, destinationUrl) {
    // Keyed by destination so dismissing a banner for one destination does not
    // silence a (different) later recommendation.
    return LS_DISMISS_PREFIX + store + '_' + (hostOf(destinationUrl) || 'none');
  }

  function bannerDismissed(store, destinationUrl) {
    try {
      var raw = lsGet(dismissKey(store, destinationUrl));
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (!ts) return false;
      if (Date.now() - ts > DISMISS_TTL_MS) {
        // Expired: clear it so the banner can show again.
        lsSet(dismissKey(store, destinationUrl), '');
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function rememberDismissal(store, destinationUrl) {
    lsSet(dismissKey(store, destinationUrl), String(Date.now()));
  }

  // --- banner: multilingual copy + labels (self-contained, no external deps) -

  // Short banner strings keyed by language (primary subtag or full). English is
  // the fallback. {country} and {store} are interpolated. A custom banner_text
  // set in the Redirects tab overrides the body line; the rest stays localized.
  var BANNER_I18N = {
    en: { title: 'Shopping from {country}?', body: 'Get local pricing, shipping and support on {store}.', cta: 'Continue to {store}', stay: 'Stay here' },
    de: { title: 'Sie kaufen aus {country} ein?', body: 'Lokale Preise, Versand und Support auf {store}.', cta: 'Weiter zu {store}', stay: 'Hier bleiben' },
    fr: { title: 'Vous achetez depuis {country} ?', body: 'Prix, livraison et assistance locaux sur {store}.', cta: 'Continuer vers {store}', stay: 'Rester ici' },
    nl: { title: 'Winkelt u vanuit {country}?', body: 'Lokale prijzen, verzending en support op {store}.', cta: 'Ga naar {store}', stay: 'Hier blijven' },
    it: { title: 'Acquisti da {country}?', body: 'Prezzi, spedizione e assistenza locali su {store}.', cta: 'Continua su {store}', stay: 'Resta qui' },
    es: { title: '¿Compras desde {country}?', body: 'Precios, envío y soporte locales en {store}.', cta: 'Continuar a {store}', stay: 'Quedarme aquí' },
    pt: { title: 'A comprar de {country}?', body: 'Preços, envio e apoio locais em {store}.', cta: 'Continuar para {store}', stay: 'Ficar aqui' },
    sv: { title: 'Handlar du från {country}?', body: 'Lokala priser, frakt och support på {store}.', cta: 'Fortsätt till {store}', stay: 'Stanna kvar' },
    da: { title: 'Handler du fra {country}?', body: 'Lokale priser, levering og support på {store}.', cta: 'Fortsæt til {store}', stay: 'Bliv her' },
    no: { title: 'Handler du fra {country}?', body: 'Lokale priser, frakt og støtte på {store}.', cta: 'Fortsett til {store}', stay: 'Bli her' },
    fi: { title: 'Asioitko maasta {country}?', body: 'Paikalliset hinnat, toimitus ja tuki: {store}.', cta: 'Jatka: {store}', stay: 'Pysy tässä' },
    pl: { title: 'Kupujesz z: {country}?', body: 'Lokalne ceny, wysyłka i wsparcie na {store}.', cta: 'Przejdź do {store}', stay: 'Zostań tutaj' },
    el: { title: 'Αγοράζετε από {country};', body: 'Τοπικές τιμές, αποστολή και υποστήριξη στο {store}.', cta: 'Συνέχεια στο {store}', stay: 'Παραμονή εδώ' },
    cs: { title: 'Nakupujete z {country}?', body: 'Místní ceny, doprava a podpora na {store}.', cta: 'Pokračovat na {store}', stay: 'Zůstat zde' },
    ro: { title: 'Cumpărați din {country}?', body: 'Prețuri, livrare și asistență locale pe {store}.', cta: 'Continuați la {store}', stay: 'Rămâneți aici' },
    hu: { title: '{country} területéről vásárol?', body: 'Helyi árak, szállítás és támogatás itt: {store}.', cta: 'Tovább: {store}', stay: 'Maradok itt' },
    bg: { title: 'Пазарувате от {country}?', body: 'Локални цени, доставка и поддръжка в {store}.', cta: 'Към {store}', stay: 'Остани тук' },
    hr: { title: 'Kupujete iz {country}?', body: 'Lokalne cijene, dostava i podrška na {store}.', cta: 'Nastavi na {store}', stay: 'Ostani ovdje' },
    sk: { title: 'Nakupujete z {country}?', body: 'Miestne ceny, doprava a podpora na {store}.', cta: 'Pokračovať na {store}', stay: 'Zostať tu' },
    sl: { title: 'Nakupujete iz {country}?', body: 'Lokalne cene, dostava in podpora na {store}.', cta: 'Nadaljuj na {store}', stay: 'Ostani tukaj' },
    lt: { title: 'Perkate iš {country}?', body: 'Vietinės kainos, pristatymas ir pagalba {store}.', cta: 'Tęsti į {store}', stay: 'Likti čia' },
    lv: { title: 'Iepērkaties no {country}?', body: 'Vietējās cenas, piegāde un atbalsts {store}.', cta: 'Turpināt uz {store}', stay: 'Palikt šeit' },
    et: { title: 'Kas ostlete riigist {country}?', body: 'Kohalikud hinnad, tarne ja tugi: {store}.', cta: 'Jätka: {store}', stay: 'Jää siia' },
    tr: { title: '{country} ülkesinden mi alışveriş yapıyorsunuz?', body: '{store} üzerinde yerel fiyat, kargo ve destek.', cta: '{store} adresine devam et', stay: 'Burada kal' }
  };

  // Compact English country names for the routed countries (fallback = code).
  var BANNER_COUNTRY_NAMES = {
    AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', CH: 'Switzerland', CY: 'Cyprus',
    CZ: 'Czechia', DE: 'Germany', DK: 'Denmark', EE: 'Estonia', ES: 'Spain',
    FI: 'Finland', FR: 'France', GR: 'Greece', HR: 'Croatia', HU: 'Hungary',
    IE: 'Ireland', IT: 'Italy', LI: 'Liechtenstein', LT: 'Lithuania', LU: 'Luxembourg',
    LV: 'Latvia', MC: 'Monaco', MT: 'Malta', NL: 'Netherlands', NO: 'Norway',
    PL: 'Poland', PT: 'Portugal', RO: 'Romania', SE: 'Sweden', SI: 'Slovenia',
    SK: 'Slovakia', TR: 'Turkey', GB: 'the UK', IM: 'Isle of Man', GG: 'Guernsey',
    JE: 'Jersey', AU: 'Australia', NZ: 'New Zealand', CK: 'Cook Islands'
  };

  // Host -> friendly store name.
  var BANNER_STORE_LABELS = {
    'speediance.eu': 'Speediance Europe', 'speediance.co.uk': 'Speediance UK',
    'speediance.com.au': 'Speediance Australia', 'speediance.nz': 'Speediance New Zealand',
    'speediance.co.nz': 'Speediance New Zealand', 'speediance.at': 'Speediance Austria',
    'speediance.be': 'Speediance Belgium', 'speediance.ch': 'Speediance Switzerland',
    'speediance.fr': 'Speediance France', 'speediance.ie': 'Speediance Ireland',
    'speediance.nl': 'Speediance Netherlands', 'speediance.it': 'Speediance Italy',
    'speediancefitness.es': 'Speediance Spain', 'speediance.es': 'Speediance Spain'
  };

  // Best banner language for this visitor (navigator.languages), fallback en.
  function bannerLang() {
    try {
      var list = (navigator.languages && navigator.languages.length)
        ? navigator.languages : [navigator.language || ''];
      for (var i = 0; i < list.length; i++) {
        var code = String(list[i] || '').toLowerCase();
        if (BANNER_I18N[code]) return code;
        var p = code.split('-')[0];
        if (BANNER_I18N[p]) return p;
      }
    } catch (e) { /* ignore */ }
    return 'en';
  }

  // Country flag emoji from an ISO alpha-2 code (regional indicator symbols).
  function flagFromCode(code) {
    try {
      var c = String(code || '').toUpperCase();
      if (!/^[A-Z]{2}$/.test(c)) return '🌍';
      return String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65, 0x1F1E6 + c.charCodeAt(1) - 65);
    } catch (e) { return '🌍'; }
  }

  function bannerCountryName(code) {
    var up = String(code || '').toUpperCase();
    return BANNER_COUNTRY_NAMES[up] || up;
  }

  function storeLabel(url) {
    var host = (hostOf(url) || '').replace(/^www\./, '');
    return BANNER_STORE_LABELS[host] || host || 'our local store';
  }

  function fmtBanner(t, country, store) {
    return String(t).replace(/\{country\}/g, country).replace(/\{store\}/g, store);
  }

  // Small inline-SVG trust strip (pricing / shipping / support). No font dep.
  function bannerTrustSvg() {
    var a = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">';
    var tag = a + '<path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.2"/></svg>';
    var truck = a + '<path d="M3 6h11v9H3zM14 9h4l3 3v3h-7"/><circle cx="7" cy="18" r="1.6"/><circle cx="17" cy="18" r="1.6"/></svg>';
    var support = a + '<path d="M4 13v-1a8 8 0 0 1 16 0v1"/><path d="M4 14h3v5H6a2 2 0 0 1-2-2zM20 14h-3v5h1a2 2 0 0 0 2-2z"/></svg>';
    return tag + truck + support;
  }

  // Inject the banner stylesheet once (self-contained; theme may override).
  function injectGeoStyle() {
    try {
      if (document.getElementById('bm-geo-style')) return;
      var st = document.createElement('style');
      st.id = 'bm-geo-style';
      st.textContent =
        '#bm-geo-banner{position:fixed;left:0;right:0;bottom:0;z-index:2147483000;display:flex;justify-content:center;padding:16px;pointer-events:none;font-family:inherit}' +
        '#bm-geo-banner .bm-card{pointer-events:auto;width:100%;max-width:466px;background:#fff;color:#0A0A0A;border:1px solid rgba(10,10,10,.10);border-radius:18px;padding:18px;box-shadow:0 14px 44px rgba(0,0,0,.18);position:relative;animation:bmgeoUp .5s cubic-bezier(.2,.7,.3,1);box-sizing:border-box}' +
        '#bm-geo-banner .bm-card *{box-sizing:border-box}' +
        '#bm-geo-banner .bm-x{position:absolute;top:10px;right:10px;border:none;background:transparent;color:rgba(10,10,10,.55);cursor:pointer;padding:6px;line-height:0;border-radius:8px}' +
        '#bm-geo-banner .bm-x:hover{background:rgba(10,10,10,.06)}' +
        '#bm-geo-banner .bm-row{display:flex;gap:14px;align-items:flex-start;padding-right:22px}' +
        '#bm-geo-banner .bm-flag{flex:none;width:46px;height:46px;border-radius:50%;background:rgba(17,99,255,.08);display:flex;align-items:center;justify-content:center;font-size:24px;line-height:1}' +
        '#bm-geo-banner .bm-title{font-size:16px;font-weight:600;letter-spacing:-.01em;margin:0}' +
        '#bm-geo-banner .bm-body{font-size:13.5px;line-height:1.5;color:rgba(10,10,10,.62);margin:3px 0 0}' +
        '#bm-geo-banner .bm-trust{display:flex;gap:13px;margin-top:11px;color:rgba(10,10,10,.5)}' +
        '#bm-geo-banner .bm-btns{display:flex;gap:10px;margin-top:16px}' +
        '#bm-geo-banner .bm-stay{flex:1;height:42px;border-radius:10px;border:1px solid rgba(10,10,10,.18);background:transparent;color:#0A0A0A;font-size:14px;font-weight:600;cursor:pointer}' +
        '#bm-geo-banner .bm-stay:hover{background:rgba(10,10,10,.04)}' +
        '#bm-geo-banner .bm-go{flex:1.6;height:42px;border-radius:10px;border:none;background:#1166FF;color:#fff;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:7px;text-decoration:none}' +
        '#bm-geo-banner .bm-go:hover{background:#0d56e0}' +
        '@keyframes bmgeoUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}' +
        '@keyframes bmgeoDown{from{opacity:0;transform:translateY(-100%)}to{opacity:1;transform:none}}' +
        '@media (max-width:560px){#bm-geo-banner{padding:0}#bm-geo-banner .bm-card{max-width:none;border-radius:18px 18px 0 0;box-shadow:0 -8px 30px rgba(0,0,0,.16)}#bm-geo-banner .bm-btns{flex-direction:column-reverse}#bm-geo-banner .bm-go,#bm-geo-banner .bm-stay{flex:1}}' +
        '@media (min-width:561px){#bm-geo-banner{top:0;bottom:auto;padding:0}#bm-geo-banner .bm-card{max-width:none;width:100%;border:none;border-radius:0;border-bottom:1px solid rgba(250,250,250,.10);background:#0A0A0A;color:#FAFAFA;box-shadow:0 4px 22px rgba(0,0,0,.30);display:flex;flex-direction:row;align-items:center;gap:18px;padding:13px 28px;animation:bmgeoDown .45s cubic-bezier(.2,.7,.3,1)}#bm-geo-banner .bm-x{position:static;order:3;flex:none;color:rgba(250,250,250,.6)}#bm-geo-banner .bm-x:hover{background:rgba(250,250,250,.10)}#bm-geo-banner .bm-row{order:1;flex:1;min-width:0;padding-right:0;align-items:center}#bm-geo-banner .bm-flag{width:42px;height:42px;background:rgba(250,250,250,.10)}#bm-geo-banner .bm-title{color:#FAFAFA;font-size:15px}#bm-geo-banner .bm-body{color:rgba(250,250,250,.72);font-size:13px}#bm-geo-banner .bm-trust{display:none}#bm-geo-banner .bm-btns{order:2;margin-top:0;flex:none;gap:10px}#bm-geo-banner .bm-stay{height:40px;flex:none;padding:0 16px;color:#FAFAFA;border:1px solid rgba(250,250,250,.32);background:transparent}#bm-geo-banner .bm-stay:hover{background:rgba(250,250,250,.08)}#bm-geo-banner .bm-go{height:40px;flex:none;padding:0 20px}}' +
        '.bm-geo-sel{position:relative;display:inline-block;font-family:inherit;vertical-align:middle}' +
        '.bm-geo-sel-trigger{display:inline-flex;align-items:center;gap:6px;background:transparent;border:none;color:inherit;font:inherit;font-size:13px;line-height:1;cursor:pointer;padding:6px 8px;border-radius:8px}' +
        '.bm-geo-sel-trigger:hover{background:rgba(127,127,127,.12)}' +
        '.bm-geo-sel .bm-geo-fl{font-size:15px;line-height:1}' +
        '.bm-geo-sel-label{white-space:nowrap}' +
        '.bm-geo-cv{display:inline-flex;opacity:.6}' +
        '.bm-geo-menu{position:fixed;left:0;top:0;width:266px;max-width:calc(100vw - 16px);background:#fff;color:#0A0A0A;border:1px solid rgba(10,10,10,.10);border-radius:14px;box-shadow:0 16px 40px rgba(0,0,0,.16);padding:6px 0;z-index:2147483001;max-height:78vh;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;text-align:left}' +
        '.bm-geo-menu[hidden]{display:none}' +
        '.bm-geo-mlabel{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:rgba(10,10,10,.45);padding:9px 14px 4px}' +
        '.bm-geo-item{display:flex;align-items:center;gap:11px;width:100%;text-align:left;background:transparent;border:none;font:inherit;font-size:14px;color:#0A0A0A;padding:9px 14px;cursor:pointer}' +
        '@media (max-width:749px){.bm-geo-item{padding:13px 14px;font-size:15px}.bm-geo-menu{width:min(300px,calc(100vw - 24px))}}' +
        '.bm-geo-item:hover{background:rgba(17,99,255,.06)}' +
        '.bm-geo-item .bm-geo-fl{font-size:18px;width:22px;text-align:center}' +
        '.bm-geo-item .bm-geo-nm{flex:1}' +
        '.bm-geo-item .bm-geo-ck{margin-left:auto;color:#1166FF;display:flex}' +
        '.bm-geo-item.cur{font-weight:600}' +
        '.bm-geo-div{height:1px;background:rgba(10,10,10,.08);margin:4px 0}' +
        'a[href^="tel:"]{white-space:nowrap}' +
        '@media (max-width:749px){.bm-geo-sel-label{display:none}.bm-geo-sel-trigger{gap:4px;padding:6px 6px}}';
      (document.head || document.documentElement).appendChild(st);
    } catch (e) { warn('geo style inject failed', e); }
  }

  function removeBanner(wrap) {
    try { if (wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap); } catch (e) { /* ignore */ }
  }

  function handleBanner(cfg, lookup) {
    var dest = lookup.destination_url || '';
    if (!dest) return;                                   // nothing to point at.
    if (bannerDismissed(cfg.store, dest)) return;        // respect prior dismissal.
    if (document.getElementById('bm-geo-banner')) return; // no duplicate.

    var lang = bannerLang();
    var L = BANNER_I18N[lang] || BANNER_I18N.en;
    var country = bannerCountryName(lookup.country);
    var store = storeLabel(dest);
    var title = fmtBanner(L.title, country, store);
    // Custom banner_text from the tab wins; else the localized default body.
    var body = lookup.banner_text ? String(lookup.banner_text) : fmtBanner(L.body, country, store);
    var cta = fmtBanner(L.cta, country, store);

    injectGeoStyle();

    var wrap = document.createElement('div');
    wrap.id = 'bm-geo-banner';
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', 'Regional store suggestion');
    wrap.setAttribute('lang', lang);

    var card = document.createElement('div');
    card.className = 'bm-card';

    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'bm-x';
    x.setAttribute('aria-label', 'Dismiss');
    x.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    x.addEventListener('click', function () { rememberDismissal(cfg.store, dest); removeBanner(wrap); });
    card.appendChild(x);

    var row = document.createElement('div');
    row.className = 'bm-row';
    var fl = document.createElement('div');
    fl.className = 'bm-flag';
    fl.textContent = flagFromCode(lookup.country);
    row.appendChild(fl);

    var col = document.createElement('div');
    col.style.minWidth = '0';
    var h = document.createElement('div');
    h.className = 'bm-title';
    h.textContent = title;                                // textContent: safe.
    col.appendChild(h);
    var b = document.createElement('div');
    b.className = 'bm-body';
    b.textContent = body;
    col.appendChild(b);
    var tr = document.createElement('div');
    tr.className = 'bm-trust';
    tr.setAttribute('aria-hidden', 'true');
    tr.innerHTML = bannerTrustSvg();                      // static SVG strings.
    col.appendChild(tr);
    row.appendChild(col);
    card.appendChild(row);

    var btns = document.createElement('div');
    btns.className = 'bm-btns';
    var stayBtn = document.createElement('button');
    stayBtn.type = 'button';
    stayBtn.className = 'bm-stay';
    stayBtn.textContent = L.stay;
    stayBtn.addEventListener('click', function () { rememberDismissal(cfg.store, dest); removeBanner(wrap); });
    var go = document.createElement('a');
    go.className = 'bm-go';
    go.href = dest;
    go.setAttribute('rel', 'nofollow');                  // no link equity to bots.
    var goSpan = document.createElement('span');
    goSpan.textContent = cta;
    go.appendChild(goSpan);
    var arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.style.cssText = 'display:flex';
    arrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
    go.appendChild(arrow);
    go.addEventListener('click', function () { rememberDismissal(cfg.store, dest); });
    btns.appendChild(stayBtn);
    btns.appendChild(go);
    card.appendChild(btns);

    wrap.appendChild(card);
    try {
      document.body.appendChild(wrap);
    } catch (e) {
      warn('banner append failed', e);
    }
  }

  // Dispatch on the lookup action. Returns true ONLY if a redirect was actually
  // performed, so the caller can skip the in-page language switch (we are
  // navigating away anyway).
  function act(cfg, lookup) {
    if (!lookup || typeof lookup !== 'object') return false;
    var action = lookup.action;

    if (action === 'redirect') {
      return handleRedirect(cfg, lookup);
    }
    if (action === 'banner') {
      handleBanner(cfg, lookup);
      return false;
    }
    // 'off' or anything unrecognized: do nothing (fail safe).
    return false;
  }

  // ---------------------------------------------------------------------------
  // 4b. Browser-language auto-switch (within the current market)
  // ---------------------------------------------------------------------------
  //
  // After the country/store routing has settled (no redirect pending), switch
  // the page to the visitor's preferred browser language IF this market
  // actually publishes it. We read the locale options from the page's OWN
  // same-host hreflang alternates (what Shopify advertises for THIS market),
  // so we never navigate to an unpublished locale and 404. Single-writer rule
  // still holds: we change locale by navigating Shopify's locale URL, never by
  // writing the localization cookie.

  // Current locale path prefix, e.g. 'fr' on /fr/products/x, '' on the default.
  function currentLocalePrefix() {
    try {
      var m = (window.location.pathname || '').match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
      return m ? m[1].toLowerCase() : '';
    } catch (e) {
      return '';
    }
  }

  // Locale path prefix carried by an hreflang href, e.g. 'nl' for
  // https://speediance.be/nl, '' for https://speediance.be/.
  function localePrefixFromUrl(href) {
    try {
      var u = new URL(href, window.location.href);
      var m = (u.pathname || '').match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
      return m ? m[1].toLowerCase() : '';
    } catch (e) {
      return '';
    }
  }

  // Apply a locale prefix to the CURRENT path, preserving page/query/hash.
  // prefix '' => primary locale (no prefix). e.g. on /fr/products/x with prefix
  // 'de' => /de/products/x; with prefix '' => /products/x.
  function applyLocalePrefix(prefix) {
    var path = window.location.pathname || '/';
    path = path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '');
    if (!path) path = '/';
    var base = prefix ? '/' + prefix : '';
    var target = base + (path.charAt(0) === '/' ? path : '/' + path);
    if (!target) target = '/';
    target += (window.location.search || '') + (window.location.hash || '');
    return target;
  }

  // Same-host locale alternates advertised by this page's hreflang tags.
  function sameHostLocaleAlternates() {
    var out = [];
    try {
      var here = (window.location.host || '').toLowerCase();
      var links = document.querySelectorAll('link[rel="alternate"][hreflang]');
      for (var i = 0; i < links.length; i++) {
        var hl = (links[i].getAttribute('hreflang') || '').toLowerCase();
        var href = links[i].getAttribute('href') || '';
        if (!hl || hl === 'x-default' || !href) continue;
        if (hostOf(href) !== here) continue; // only switch WITHIN this market.
        out.push({ lang: hl, prefix: localePrefixFromUrl(href) });
      }
    } catch (e) { /* ignore */ }
    return out;
  }

  function browserLangPrefs() {
    try {
      var list = (navigator.languages && navigator.languages.length)
        ? navigator.languages
        : [navigator.language || ''];
      return list.map(function (x) { return String(x || '').toLowerCase(); }).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  // Pick the best same-host alternate for the visitor's browser languages:
  // exact hreflang match first (e.g. 'fr-be'), then primary-subtag match
  // (e.g. browser 'fr' / 'fr-fr' matches alt 'fr-be'). Returns an alt or null.
  function pickLanguageAlternate(alts, prefs) {
    for (var p = 0; p < prefs.length; p++) {
      var pref = prefs[p];
      for (var a = 0; a < alts.length; a++) {
        if (alts[a].lang === pref) return alts[a];
      }
    }
    for (var p2 = 0; p2 < prefs.length; p2++) {
      var primary = prefs[p2].split('-')[0];
      for (var a2 = 0; a2 < alts.length; a2++) {
        if (alts[a2].lang.split('-')[0] === primary) return alts[a2];
      }
    }
    return null;
  }

  // Switch the page to the visitor's browser language within this market, once.
  function maybeAutoLanguage(cfg) {
    try {
      var guardKey = SS_LANG_GUARD + (cfg && cfg.store ? cfg.store : '');
      if (ssGet(guardKey)) return;              // already auto-switched this session.
      if (lsGet(LS_LANG_CHOICE)) return;        // visitor chose a language explicitly.
      if ((getQueryParam('stay') || '') === '1') return; // explicit "leave me alone".

      var alts = sameHostLocaleAlternates();
      if (alts.length < 2) return;              // single-locale market: nothing to do.

      var prefs = browserLangPrefs();
      if (!prefs.length) return;

      var best = pickLanguageAlternate(alts, prefs);
      if (!best) return;

      if (best.prefix === currentLocalePrefix()) return; // already in this language.

      ssSet(guardKey, '1');
      window.location.replace(applyLocalePrefix(best.prefix));
    } catch (e) {
      warn('auto-language failed', e);
    }
  }

  // ---------------------------------------------------------------------------
  // 5. Boot
  // ---------------------------------------------------------------------------

  function boot() {
    var cfg;
    try {
      cfg = readConfig();
    } catch (e) {
      warn('config read failed; going inert', e);
      return;
    }

    // Kill-switch: ?geo=off / data-geo-enabled="false" / missing store|apiBase.
    if (killSwitchActive(cfg)) {
      // Inert. Any residual Orbe (during parallel-run) still handles things.
      return;
    }

    // Persist a manual market pick: if the visitor arrived via the header
    // selector (?geo_pick=1), remember it so we never auto-redirect them away
    // from the market they chose.
    if ((getQueryParam('geo_pick') || '') === '1') {
      lsSet(LS_MARKET_CHOICE, String(Date.now()));
    }

    // Fire the personalized lookup and the cacheable config IN PARALLEL. The
    // redirect decision must not wait on the config round-trip, otherwise a
    // visitor sees a flash-of-wrong-region before the bounce. The geo-lookup
    // endpoint already returns action:'off' when the store is disabled or the
    // kill-switch is set, so acting on the lookup alone is safe.

    // 1. geo-lookup -> act (redirect as early as possible). If NO redirect was
    //    performed, consider switching the page language to the browser's.
    loadGeoLookup(cfg).then(function (lookup) {
      var didRedirect = false;
      try { didRedirect = act(cfg, lookup); } catch (e) { warn('act failed', e); }
      if (!didRedirect) {
        try { maybeAutoLanguage(cfg); } catch (e) { warn('auto-language failed', e); }
      }
    }).catch(function (err) {
      // Network / parse failure on lookup: fail safe (no redirect happened, so
      // the in-page language switch can still run).
      warn('geo-lookup failed; doing nothing', err);
      try { maybeAutoLanguage(cfg); } catch (e) { /* ignore */ }
    });

    // 2. geo-config -> render the header selectors. Independent of the lookup.
    loadGeoConfig(cfg).then(function (config) {
      // Server-side per-store kill-switch (enabled:false): skip rendering.
      if (config && config.enabled === false) return;
      try { renderCountrySelector(config); } catch (e) { warn('country render', e); }
      try { renderLanguageSelector(config); } catch (e) { warn('language render', e); }
    }).catch(function (err) {
      warn('geo-config failed; selectors skipped', err);
    });
  }

  // Run as early as possible so a redirect happens before heavy paint, but the
  // banner needs document.body. We gate on readiness defensively.
  function start() {
    try {
      // The redirect decision does not need the DOM; selectors/banner do.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
      } else {
        boot();
      }
    } catch (e) {
      warn('start failed', e);
    }
  }

  start();
})();
